<template>
  <div class="container your-campaign">
    <fieldset :disabled="!isEditable">
      <div class="row">
        <div>
          <div class="col-md-12 mt-50">
            <form-error :errors='errors' :attribute="'title'">
              <input v-model="project.title" class="form-control" id="campaign-title" :placeholder="$t('project.campaignTitle')">
            </form-error>
          </div><!-- .col-md-12 -->
          <div class="col-md-3">
            <form-error :errors='errors' :attribute="'city'">
              <input v-model="project.city" class="form-control" :placeholder="$t('project.city')">
            </form-error>
          </div><!-- .col-md-3 -->
          <div class="col-md-3">
            <form-error :errors='errors' :attribute="'country'">
              <select v-if="countries.length" v-model="project.country_id" class="form-control" ref="countrySelect">
                <option disabled value="">{{ $t("country") }}</option>
                <option v-for="country in countries" :value="country.id">{{ country.name }}</option>
              </select>
            </form-error>
          </div><!-- .col-md-3 -->
          <div class="col-md-6">
            <form-error :errors='errors' :attribute="'category'">
              <select v-if="categories.length" v-model="project.category_id" class="form-control">
                <option disabled value="">{{ $t("project.form.category") }}</option>
                <option v-for="category in categories" :value="category.id">{{ category.name }}</option>
              </select>
            </form-error>
          </div>
          <div class="col-md-3">
            <form-error :errors='errors' :attribute="'to_collect'">
              <label class="sr-only" for="campaign-goal">{{ $t("project.form.amount") }}</label>
              <div class="input-group">
                <div class="input-group-addon">{{ $t("project.form.goal") }}</div>
                <input v-model.number="project.to_collect" type="number" class="form-control" :placeholder="$t('project.form.amount')">
              </div>
            </form-error>
          </div><!-- .col-md-3 -->
          <div class="col-md-3">
            <form-error :errors='errors' :attribute="'currency'">
              <input-radio v-for="currency in currencies"
                :value="currency"
                v-model="project.currency"
                :name="'campaign-currency'"
                style="display: inline"
                :key="currency">
                <b slot="label">
                  {{ currency }}
                </b>
              </input-radio>
              <p style="font-size: 0.85em">{{ $t('project.currencyInfo') }}</p>
            </form-error>
          </div><!-- .col-md-3 -->
          <div class="col-md-3">
            <form-error :errors='errors' :attribute="'duration'">
              <label class="sr-only" for="campaign-deadline">{{ $t("project.duration") }}</label>
              <div class="input-group">
                <div class="input-group-addon">{{ $t("project.duration") }}:</div>
                <input v-model.number="project.duration" type="number" class="form-control" :placeholder="$t('days')">
              </div>
            </form-error>
          </div><!-- .col-md-2 -->
          <div v-if="project.crowdfunding" class="col-md-3 days-max">
            <p>{{ $t("project.form.launch") }}</p>
          </div><!-- .col-md-4 -->
          <div class="col-md-12">
            <div>
              <form-error v-if="project.crowdfunding" :errors='errors' :attribute="'withdrawal_iban'">
                <input v-model="project.withdrawal_iban" type="text" class="form-control" :placeholder="$t('project.bank')">
              </form-error>
              <p v-else>{{ $t("project.form.fundraisingIbanInfo") }}</p>
            </div>
            <div>
              <div style="display: flex;align-items: baseline">
                <p>{{ $t('project.delivery.delivery') }}</p>
                <input-radio v-for="delivery in deliveryOptions"
                  :value="delivery"
                  v-model="project.delivery"
                  name="reward-delivery"
                  style="display: inline"
                  :key="delivery">
                  <b slot="label">
                    {{ $t(`project.delivery.${delivery}`) }}
                  </b>
                </input-radio>
              </div>
            </div>
            <button v-if='showSaveButton() && isEditable'
              @click="submit.action"
              :disabled="submit.processing"
              type="button"
              :class="'btn btn-default btn-lg uppercase button-wide-' + crowd_or_rais">
              <span v-if="!submit.processing">{{ $t("save") }}</span>
              <clip-loader :loading="submit.processing" :color="'#fff'"></clip-loader>
            </button>
          </div>
        </div>
      </div>
    </fieldset>
  </div><!-- .container mb-30 your-campaign -->
</template>

<script>
import dictionariesApi from '@/api/v1/shared/dictionaries';
import projectsApi from '@/api/v1/projects';
import organizationsApi from '@/api/v1/organizations';
import userOrganizationApi from '@/api/v1/user';
import FormError from '@/components/FormError';
import InputRadio from '@/components/Utils/Inputs/Radio';
import ClipLoader from 'vue-spinner/src/ClipLoader';
import projectModel from '@/models/project';

const currencies = dictionariesApi.currencies();
const deliveryOptions = dictionariesApi.rewardDeliveryOptions();

export default {
  props: {
    project: {
      type: Object,
      default: projectModel.project,
    },
    errors: Object,
    submit: {
      action: Function,
      processing: Boolean,
    },
  },
  components: { FormError, ClipLoader, InputRadio },
  data() {
    return {
      categories: [],
      countries: [],
      organizations: [],
      currencies,
      deliveryOptions,
      userOrganization: {},
      organization: {
        name: this.$t('organization.organization'),
      },
    };
  },
  computed: {
    isEditable() {
      return !this.project.status || this.project.status === 'created';
    },
    crowd_or_rais() {
      return this.project.crowdfunding ? 'crowd' : 'rais';
    },
  },
  methods: {
    selectOrganization(option) {
      this.project.organization_id = option.id;
      this.organization = option;
    },
    pickUserOrganization() {
      this.project.organization_id = this.userOrganization.id;
      this.organization = { name: this.$t('organization.organization') };
    },
    showSaveButton() {
      return !this.project.id;
    },
  },
  created() {
    userOrganizationApi.getOrganization((organization) => {
      this.userOrganization = organization;
    });
    dictionariesApi.getCountries((countries) => {
      this.countries = countries;
    });
    const isCrowdfunding = this.project.crowdfunding || this.$route.name.includes('crowdfunding');
    projectsApi.getCategories((categories) => {
      this.categories = categories;
    }, isCrowdfunding);
    if (!this.project.crowdfunding) {
      organizationsApi.getOrganizations((organizations) => {
        this.organizations = organizations;
      });
    }
  },
};
</script>

<style src='vue-multiselect/dist/vue-multiselect.min.css'></style>



// WEBPACK FOOTER //
// Basic.vue?57a85972